import { MembershipSign } from 'utils/filter/filter';
import buildNumberFilterFunction from './numberFilterFunction';
import buildStringFilterFunction from './stringFilterFunction';
import buildTimeFilterFunction from './timeFilterFunction';
import buildDateFilterFunction from './dateFilterFunction';
import buildEnumFilterFunction from './enumFilterFunction';

import {
    ColumnFilterState,
    FilterState,
    isColumnFilter,
    isRowFilter,
    RowFilterState,
} from 'widgets/store/grid/filters/FilterState';
import {
    ColumnDefinitionsDictionary,
    ValueType,
} from '@instinet-analytics/semantic-ui-components';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ColumnFiltersFunction = (source: any) => boolean;

export function buildFiltersFunction<TColumnId extends string>(
    columnDefinitions: ColumnDefinitionsDictionary<TColumnId>,
    filters: FilterState<TColumnId>[],
    exchangeRate: number
): ColumnFiltersFunction {
    const columnFilters = filters.filter(isColumnFilter).map((columnFilter) => {
        const column = (columnFilter as ColumnFilterState<TColumnId>).column;

        return {
            column,
            filterFunction: buildColumnFilterFunction(
                columnDefinitions,
                columnFilter as ColumnFilterState<TColumnId>,
                exchangeRate
            ),
            dataField: columnDefinitions[column].field,
        };
    });

    const columnsFilterFunction = columnFilters.length
        ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (entity: any) => {
              for (const columnFilter of columnFilters) {
                  if (
                      !columnFilter.filterFunction(
                          entity[columnFilter.dataField]
                      )
                  ) {
                      return false;
                  }
              }
              return true;
          }
        : undefined;

    const rowFilter = filters.find((f) => isRowFilter(f)) as
        | RowFilterState
        | undefined;

    const rowFilterFunction = rowFilter
        ? buildRowFilterFunction(rowFilter)
        : undefined;

    if (columnsFilterFunction && rowFilterFunction) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (entity: any) =>
            columnsFilterFunction(entity) && rowFilterFunction(entity);
    } else if (columnsFilterFunction) {
        return columnsFilterFunction;
    } else if (rowFilterFunction) {
        return rowFilterFunction;
    } else {
        return () => true;
    }
}

function buildColumnFilterFunction<TColumnId extends string>(
    columnDefinitions: ColumnDefinitionsDictionary<TColumnId>,
    columnFilter: ColumnFilterState<TColumnId>,
    exchangeRate: number
): ColumnFiltersFunction {
    const { column, filter } = columnFilter;
    const columnDefinition = columnDefinitions[column];

    const columnExchangeRate =
        columnDefinition.valueType === ValueType.CURRENCY
            ? exchangeRate
            : undefined;

    switch (filter.valueType) {
        case ValueType.NUMBER:
            return buildNumberFilterFunction(
                filter,
                columnDefinition.precision ?? 0,
                columnExchangeRate || 1
            );
        case ValueType.STRING:
            return buildStringFilterFunction(filter);
        case ValueType.TIME:
            return buildTimeFilterFunction(filter);
        case ValueType.DATE:
        case ValueType.DATETIME:
            return buildDateFilterFunction(filter);
        case ValueType.ENUM:
            return buildEnumFilterFunction(filter);
        default:
            return () => true;
    }
}

function buildRowFilterFunction(rowFilter: RowFilterState) {
    const shouldBeInList = rowFilter.sign === MembershipSign.INCLUDE;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (entity: any) => {
        return shouldBeInList === rowFilter.rowIds.includes(entity.idx);
    };
}
